import React, { useContext, useEffect, useState } from "react";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { IResourceComponentsProps } from "@refinedev/core";
import { ExportButton, RefreshButton, List } from "@refinedev/mui";
import { Query } from "@refinedev/appwrite";

import Moment from "moment";
import * as XLSX from "xlsx";

import { TentantContext } from "contexts/tentant";
import { packageDetails } from "interfaces";
import { database } from "utility/appwriteClient";

export const Report: React.FC<IResourceComponentsProps> = () => {
  const [packages, setPackages] = useState<packageDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tentant] = useContext(TentantContext);
  const [date, setDate] = useState<string>("");
  const [source, setSource] = useState<string>("All");
  const [status, setStatus] = useState<string>("All");
  const [carrier, setCarrier] = useState<string>("All");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, [date]);

  useEffect(() => {
    fetchPackages();
  }, [source]);

  useEffect(() => {
    fetchPackages();
  }, [status]);

  useEffect(() => {
    fetchPackages();
  }, [carrier]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      let allResults: packageDetails[] = [];
      let hasMore = true;

      if (!date) {
        setPackages([]);
        return;
      }

      const filterDate = new Date(date);
      const startOfDay = new Date(filterDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(filterDate);
      endOfDay.setHours(23, 59, 59, 999);

      const filters = [
        Query.orderDesc("$createdAt"),
        Query.greaterThanEqual("$createdAt", startOfDay.toISOString()),
        Query.lessThan("$createdAt", endOfDay.toISOString()),
        Query.limit(1000),
      ];

      if (source && source.trim() !== "All") {
        filters.push(Query.equal("Source", source));
      }

      if (status && status.trim() !== "All") {
        filters.push(Query.equal("Status", status));
      }

      if (carrier && carrier.trim() !== "All") {
        filters.push(Query.equal("Shipment_Carrier", carrier));
      }

      while (hasMore) {
        const response = await database.listDocuments(
          tentant.toLowerCase(),
          `${tentant.toLowerCase()}_package`,
          filters
        );

        allResults.push(...(response.documents as unknown as packageDetails[]));
        hasMore = response.documents.length === 1000;
      }

      setPackages(allResults);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const rows = packages.map((item) => ({
    id: item.$id,
    orderReference: item.Order_Reference,
    shipmentId: item.Shipment_Id,
    source: item.Source,
    status: item.Status,
    createdAt: Moment(item.$createdAt).format("dddd, DD MMMM, YYYY h:mm A"),
    shipFrom: item.Ship_From,
    city: item.Address_City,
    state: item.Address_State,
    postCode: item.Address_Postcode,
    carrier: item.Shipment_Carrier,
    eta: item.Shipment_ETA,
    cost: item.Shipment_Cost,
  }));

  const columns: GridColDef[] = [
    {
      field: "orderReference",
      headerName: "Order Reference",
      flex: 1,
    },
    {
      field: "shipmentId",
      headerName: "Shipment Id",
      flex: 1,
    },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
    },
    {
      field: "shipFrom",
      headerName: "Ship From",
      flex: 1,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
    },
    {
      field: "postCode",
      headerName: "Post Code",
      flex: 1,
    },
    {
      field: "carrier",
      headerName: "Carrier",
      flex: 1,
    },
    {
      field: "eta",
      headerName: "ETA",
      flex: 1,
    },
    {
      field: "cost",
      headerName: "Cost",
      flex: 1,
    },
  ];

  const handleSourceChange = (event: SelectChangeEvent) => {
    const selectedSource = event.target.value;
    setSource(selectedSource);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const selectedStatus = event.target.value;
    setStatus(selectedStatus);
  };

  const handleCarrierChange = (event: SelectChangeEvent) => {
    const selectedCarrier = event.target.value;
    setCarrier(selectedCarrier);
  };

  const exportToExcel = () => {
    if (packages.length === 0) {
      setErrorMessage("No packages available to export.");
      setErrorModal(true);
      return;
    }

    setLoading(true);

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Packages");

    const formattedDate = Moment(date).format("YYYY_MM_DD");
    const filename = `Packages_${formattedDate}.xlsx`;

    XLSX.writeFile(workbook, filename);
    setLoading(false);
  };

  const handleResetFilters = () => {
    setDate("");
    setSource("All");
    setStatus("All");
    setCarrier("All");
    setPackages([]);
  };

  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 4,
        backgroundColor: "background.paper",
      }}
    >
      <Grid item xs={12} lg={12}>
        <List
          wrapperProps={{
            sx: {
              paddingX: { xs: 2, md: 0 },
              boxShadow: "0",
              backgroundColor: "background.paper",
              backgroundImage:
                "linear-gradient(rgba(255, 255, 255, 0.00), rgba(255, 255, 255, 0.00))",
            },
          }}
          headerProps={{
            action: (
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  sx={{
                    "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                      opacity: 0.5,
                    },
                  }}
                />
                <FormControl sx={{ m: 1, minWidth: 200 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    label="Source"
                    autoWidth
                    value={source}
                    onChange={handleSourceChange}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="OpenFreight">Open Freight</MenuItem>
                    <MenuItem value="OptimoRoute">Optimo Route</MenuItem>
                    <MenuItem value="Shippit">Shippit</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    autoWidth
                    value={status}
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Estimate">Estimate</MenuItem>
                    <MenuItem value="Lodged">Lodged</MenuItem>
                    <MenuItem value="Closed in MIS">Closed in MIS</MenuItem>
                    <MenuItem value="Error">Error</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 200 }}>
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    label="Carrier"
                    autoWidth
                    value={carrier}
                    onChange={handleCarrierChange}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="STEROAD">STEROAD</MenuItem>
                    <MenuItem value="STEFPP">STEFPP</MenuItem>
                    <MenuItem value="TOLL">TOLL</MenuItem>
                    <MenuItem value="Local Van">Local Van</MenuItem>
                  </Select>
                </FormControl>
                <ExportButton onClick={exportToExcel} />
                <RefreshButton onClick={handleResetFilters} />
              </Stack>
            ),
          }}
        >
          <Grid container>
            <Grid item xs={12} lg={12}>
              <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 50, 100]}
              />
            </Grid>
          </Grid>
        </List>
      </Grid>
      <Dialog
        open={errorModal}
        onClose={() => {
          setErrorModal(false);
          setErrorMessage(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setErrorModal(false);
              setErrorMessage(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop open={loading} sx={{ zIndex: 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
